import { DEFAULT_WALL_THICKNESS_M, getWallSegmentsResolved, normalizeDesignerOpenings } from './floorPlanDesignerState.js';
import {
  findClosedPolygons,
  openingEndpointsFromCenterRatio,
} from './floorPlanDesignerGeometry.js';
import { buildBoundingFloorPolygon } from './floorPlanEditorGeometry.js';
import { buildRoomBoundaryModel } from './floorPlanDesignerRoomBoundaries.js';
import { exportBackgroundReferenceImage } from './floorPlanBackgroundImageState.js';

/** @typedef {import('./floorPlanDesignerState.js').DesignerState} DesignerState */

/**
 * Build analysis JSON for 3D preview — does not require walls or rooms.
 * @param {DesignerState} state
 * @returns {{ success: boolean; errors: string[]; warnings: string[]; analysisJson: Record<string, unknown> | null }}
 */
export function buildDesignerPreviewJson(state) {
  const errors = [];
  const warnings = [];

  if (!Number.isFinite(state.scale.metersPerPixel) || state.scale.metersPerPixel <= 0) {
    errors.push('Scale (metersPerPixel) must be greater than zero.');
    return { success: false, errors, warnings, analysisJson: null };
  }

  const rooms = findClosedPolygons(state);
  if (rooms.length === 0 && state.walls.length >= 3) {
    warnings.push('No closed room polygon detected — 3D floor may use bounding box.');
  }

  return {
    success: true,
    errors,
    warnings,
    analysisJson: designerStateToAnalysisJson(state),
  };
}

/**
 * Validate state for export, copy, and room render — requires geometry.
 * @param {DesignerState} state
 * @returns {{ success: boolean; errors: string[]; warnings: string[]; analysisJson: Record<string, unknown> | null }}
 */
export function validateDesignerState(state) {
  const errors = [];
  const warnings = [];

  if (state.walls.length === 0) {
    errors.push('At least one wall segment is required.');
  }

  const pointCount = new Set();
  state.walls.forEach((w) => {
    pointCount.add(w.startPointId);
    pointCount.add(w.endPointId);
  });
  if (pointCount.size < 3) {
    errors.push('Please draw at least 3 connected wall points before generating the 3D preview.');
  }

  if (!Number.isFinite(state.scale.metersPerPixel) || state.scale.metersPerPixel <= 0) {
    errors.push('Scale (metersPerPixel) must be greater than zero.');
  }

  const rooms = findClosedPolygons(state);
  if (rooms.length === 0 && state.walls.length >= 3) {
    warnings.push('No closed room polygon detected — 3D floor may use bounding box.');
  }

  if (errors.length) {
    return { success: false, errors, warnings, analysisJson: null };
  }

  return {
    success: true,
    errors,
    warnings,
    analysisJson: designerStateToAnalysisJson(state),
  };
}

/**
 * @param {DesignerState} state
 * @returns {Record<string, unknown>}
 */
export function designerStateToAnalysisJson(state) {
  const normalized = normalizeDesignerOpenings(state);
  const { canvasWidth, canvasHeight, scale } = normalized;
  const segments = getWallSegmentsResolved(normalized);
  const rooms = findClosedPolygons(normalized);
  const boundaryModel = buildRoomBoundaryModel(normalized);
  const primaryPolygon = rooms[0]?.polygon || segments.flatMap((s) => [s.start, s.end]);
  const exportedRooms = boundaryModel.rooms.map((room) => {
    const meta = normalized.roomsMeta?.[room.id];
    return {
      id: room.id,
      polygon: room.polygon.map((pt) => [...pt]),
      name: meta?.name,
      floorColor: meta?.floorColor,
      floorMaterial: meta?.floorMaterial,
      visibleIn3D: meta?.visibleIn3D ?? true,
      areaSqM: room.areaSqM,
      wallIds: [...room.wallIds],
      boundaryWallIds: [...room.boundaryWallIds],
    };
  });

  const walls = segments.map((seg) => {
    const lenPx = Math.hypot(seg.end[0] - seg.start[0], seg.end[1] - seg.start[1]);
    return {
      id: seg.id,
      physicalWallId: seg.id,
      start: seg.start,
      end: seg.end,
      startPointId: seg.startPointId,
      endPointId: seg.endPointId,
      lengthPixels: lenPx,
      lengthMeters: lenPx * scale.metersPerPixel,
      thicknessMeters: seg.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M,
      color: seg.color,
      material: seg.material,
      insideMaterial: seg.insideMaterial,
      outsideMaterial: seg.outsideMaterial,
      visibleIn3D: seg.visibleIn3D ?? true,
      orientation:
        Math.abs(seg.end[0] - seg.start[0]) >= Math.abs(seg.end[1] - seg.start[1])
          ? 'horizontal'
          : 'vertical',
      confidence: 'user',
    };
  });

  const wallById = new Map(walls.map((w) => [String(w.id), w]));

  const objects = (normalized.objects || []).filter((object) => object.type === 'furniture');
  const mergedOpenings = normalized.openings.filter(
    (opening) => opening.type === 'door' || opening.type === 'window',
  );

  const exportOpenings = (type) =>
    mergedOpenings
      .filter((o) => o.type === type)
      .map((item) => {
        const seg = wallById.get(item.wallId);
        if (!seg) {
          return {
            id: item.id,
            wallId: item.wallId,
            t: item.centerRatio,
            opening: [],
            widthPixels: 0,
            widthMeters: item.widthMeters,
            centerRatio: item.centerRatio,
            heightMeters: item.heightMeters,
            sillHeightMeters: item.sillHeightMeters,
            thicknessMeters: item.thicknessMeters,
            thickness2D: item.thickness2D ?? item.thicknessMeters,
            type: type === 'door' ? 'hinged' : undefined,
            confidence: 'user',
          };
        }
        const halfPx = (item.widthMeters / scale.metersPerPixel) / 2;
        const opening = openingEndpointsFromCenterRatio(
          seg.start,
          seg.end,
          item.centerRatio,
          halfPx,
        );
        const widthPx = Math.hypot(opening[0][0] - opening[1][0], opening[0][1] - opening[1][1]);
        const base = {
          id: item.id,
          wallId: item.wallId,
          t: item.centerRatio,
          opening,
          widthPixels: widthPx,
          widthMeters: item.widthMeters,
          centerRatio: item.centerRatio,
          heightMeters: item.heightMeters,
          sillHeightMeters: item.sillHeightMeters,
          thicknessMeters: item.thicknessMeters,
          thickness2D: item.thickness2D ?? item.thicknessMeters,
          confidence: 'user',
        };
        if (type === 'door') {
          return {
            ...base,
            type: 'hinged',
            hingeSide: item.hingeSide ?? item.swingDirection ?? 'left',
            swingSide: item.swingSide ?? 'inside',
            swingDirection: item.swingDirection ?? item.hingeSide ?? 'left',
            doorState: item.doorState ?? 'closed',
          };
        }
        return base;
      });

  const polygon =
    primaryPolygon.length >= 3
      ? primaryPolygon.map((pt) => [...pt])
      : segments.flatMap((s) => [s.start, s.end]);

  const maxWidthPx =
    polygon.length >= 2
      ? Math.max(...polygon.map((p) => p[0])) - Math.min(...polygon.map((p) => p[0]))
      : canvasWidth;
  const maxDepthPx =
    polygon.length >= 2
      ? Math.max(...polygon.map((p) => p[1])) - Math.min(...polygon.map((p) => p[1]))
      : canvasHeight;

  return {
    success: true,
    source: 'manualFloorPlanDesigner',
    coordinateSystem: {
      origin: 'top-left',
      unit: 'pixels',
      imageWidth: canvasWidth,
      imageHeight: canvasHeight,
    },
    targetSpace: {
      name: 'Manual floor plan',
      type: 'room',
      isClosedRoom: rooms.length > 0,
      confidence: 'user',
      polygon,
      approxDimensionsMeters: {
        maxWidth: maxWidthPx * scale.metersPerPixel,
        maxDepth: maxDepthPx * scale.metersPerPixel,
        areaSqmApprox: rooms[0]?.areaSqM ?? maxWidthPx * maxDepthPx * scale.metersPerPixel * scale.metersPerPixel,
      },
    },
    scale: {
      pixelsPerMeter: scale.pixelsPerMeter,
      metersPerPixel: scale.metersPerPixel,
      confidence: 'high',
      source: scale.source,
    },
    points: normalized.points.map((p) => ({ id: p.id, x: p.x, y: p.y })),
    objects: objects.map((object) => ({ ...object })),
    wallSettings: normalized.wallSettings ?? {
      thicknessMeters: DEFAULT_WALL_THICKNESS_M,
      thicknessDirection: 'outside',
    },
    walls,
    doors: exportOpenings('door'),
    windows: exportOpenings('window'),
    rooms: exportedRooms,
    roomsMeta: state.roomsMeta ?? {},
    backgroundReferenceImage: exportBackgroundReferenceImage(
      normalized.backgroundReferenceImage,
      scale.metersPerPixel,
    ),
    zones: [],
    openBoundaries: [],
    camera: state.camera?.position && state.camera?.direction
      ? {
          position: state.camera.position,
          direction: state.camera.direction,
          eyeHeightMeters: state.camera.heightMeters ?? 1.55,
          fov: state.camera.fov ?? 65,
        }
      : null,
    renderFloorMode: 'bounding_rectangle',
    renderFloorBounds: buildBoundingFloorPolygon(
      polygon,
      scale.metersPerPixel > 0 ? 0.5 / scale.metersPerPixel : 40,
    ),
    warnings: [],
  };
}
